import { authService } from '@/services/authService';
import { setSessionHandlers } from '@/services/axiosInstance';
import { useFeatureStore } from '@/stores/featureStore';
import { useFilePackageStore } from '@/stores/filePackageStore';
import { useFilterStore } from '@/stores/filterStore';
import { useUserStore } from '@/stores/user';
import type { LogoutReason } from '@/types/auth.types';
import { INACTIVITY_LIMIT } from '@/utils/constant';
import { idleForMs, recordActivity } from '@/services/lastActivity';

export let logoutTimer: NodeJS.Timeout | null = null;

const clearLogoutTimer = () => {
  if (logoutTimer) {
    clearTimeout(logoutTimer);
    logoutTimer = null;
  }
};

/**
 * The one way a session ends (DGR-1835): reports it to the log-in history,
 * stops the inactivity timer and clears every per-user store. The session
 * token is read before the reset, so callers don't need to wait; only wait
 * on the returned promise when a full page load follows.
 */
export const endSession = (reason: LogoutReason, { showExpiredMessage }: { showExpiredMessage: boolean }) => {
  const reported = authService.logout(reason);
  clearLogoutTimer();
  const userStore = useUserStore.getState();
  userStore.reset();
  userStore.setInactivityTimeout(showExpiredMessage);
  useFeatureStore.getState().reset();
  useFilterStore.getState().reset();
  useFilePackageStore.getState().reset();
  return reported;
};

const scheduleLogout = (delay: number) => {
  clearLogoutTimer();
  logoutTimer = setTimeout(() => {
    // Another tab may have been active since this timer started.
    const idleFor = idleForMs() ?? INACTIVITY_LIMIT;
    if (idleFor < INACTIVITY_LIMIT) {
      scheduleLogout(INACTIVITY_LIMIT - idleFor);
      return;
    }
    void endSession('INACTIVITY', { showExpiredMessage: true });
  }, delay);
};

/** Records activity and restarts the inactivity timer, only while signed in. */
export const resetLogoutTimer = () => {
  if (!useUserStore.getState().accessToken) return;
  recordActivity();
  scheduleLogout(INACTIVITY_LIMIT);
};

/** Connects the shared axios instance to the session: activity restarts the timer, a failed refresh ends the session. Call once at startup. */
export const registerSessionHandlers = () => {
  setSessionHandlers({
    onActivity: resetLogoutTimer,
    onRefreshFailed: () => endSession('SESSION_EXPIRED', { showExpiredMessage: true }),
  });
};
